import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useIntersectionObserver } from "lib/hooks/useIntersectionObserver";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectIsResumeDownloading,
  setIsExplorePlansOverlayOpen,
} from "lib/redux/uiSlice";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

interface ExplorePlansOverlayProps {
  countryCode: string;
}

export const ExplorePlans: React.FC<ExplorePlansOverlayProps> = ({
  countryCode,
}) => {
  const dispatch = useAppDispatch();
  const isResumeDownloading = useAppSelector(selectIsResumeDownloading);
  const [countDown, setCountDown] = useState(3);
  // deterministic hourly activity for modal (40-50)
  const getHourlyActivity40to50 = (hour: number) => {
    const min = 40;
    const max = 50;
    const date = new Date().toISOString().slice(0, 10);
    let seed = 0;
    for (let c of date) seed += c.charCodeAt(0);
    const value = (seed * (hour + 1) * 9301 + 49297) % 233280;
    const normalized = value / 233280;
    return Math.floor(min + normalized * (max - min));
  };
  const modalActivity = getHourlyActivity40to50(new Date().getHours());
  const imageSrc =
    countryCode === "IN"
      ? "robot-holding-card-in-69.png"
      : "robot-holding-card-us.png";

  // Close the modal when clicking outside the popup
  const handleClickOutside = (event: MouseEvent) => {
    const modal = document.getElementById("auth-modal");
    if (modal && !modal.contains(event.target as Node)) {
      dispatch(setIsExplorePlansOverlayOpen(false));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [countDown]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div
        id="auth-modal"
        className="relative w-full bg-[#FEFEFE] shadow-lg max-md:h-full max-md:px-4 max-md:py-7 md:w-[700px] md:rounded-3xl md:p-10 lg:h-[85vh] lg:w-[920px] xl:h-[557px] xl:w-[1140px]"
      >
        {countDown <= 0 && (
          <button
            className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-black text-xl text-white md:-top-7 md:right-0 md:bg-white md:text-black"
            onClick={() => {
              window.history.back();
            }}
          >
            &times;
          </button>
        )}

        <div className="flex items-center font-montserrat text-[#4D4D4D] lg:items-start">
          <div className="hidden w-1/2 lg:block">
            <Image
              src="/assets/OverlayAndPopups/MoreWithPremium.png"
              alt="Get More with Premium"
              height={388}
              width={388}
              className="ml-3 mt-10 xl:ml-5"
            />
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="font-montserrat text-[38px] font-extrabold leading-9 tracking-tighter text-[#1A52FB] max-lg:text-center md:text-4xl lg:text-[44px] xl:text-[56px]">
              Do More <br className="md:hidden" /> with Premium
            </h2>
            <p className="text-xs font-semibold max-lg:text-center md:text-base lg:w-[77%] lg:text-base xl:text-xl">
              <span className="text-[#1A52FB]">Increase</span> your chances of
              landing an interview by{" "}
              <span className="text-[#1A52FB]">80%</span> with our premium AI
              Assistant!
            </p>
            <AnimatedElement slideFrom="left">
              <div className="flex items-start gap-1 max-md:justify-center md:gap-5">
                <div className="relative h-[173px] w-[173px] md:h-[266px] md:w-[266px] lg:hidden">
                  <Image
                    src="/assets/OverlayAndPopups/MoreWithPremium.png"
                    alt="Get More with Premium"
                    fill={true}
                  />
                </div>
                <ul className="mt-5 text-[10px] font-semibold text-[#404040] md:text-sm lg:text-base">
                  <li className="mt-2 flex w-fit items-center gap-1 rounded-lg bg-white p-2 shadow-lg">
                    <CheckCircleIcon
                      className="h-6 w-6 text-white"
                      fill="#4CAF50"
                    />
                    Special Trained Ai Assistant
                  </li>
                  <li className="mt-2 flex w-fit items-center gap-1 rounded-lg bg-white p-2 shadow-lg">
                    <CheckCircleIcon
                      className="h-6 w-6 text-white"
                      fill="#4CAF50"
                    />
                    Save Unlimited Resumes
                  </li>
                  <li className="mt-2 flex w-fit items-center gap-1 rounded-lg bg-white p-2 shadow-lg">
                    <CheckCircleIcon
                      className="h-6 w-6 text-white"
                      fill="#4CAF50"
                    />
                    Check Resume Score
                  </li>
                  <li className="mt-2 flex w-fit flex-col rounded-lg bg-white p-2 shadow-lg">
                    <div className="flex items-center gap-1">
                      <CheckCircleIcon
                        className="h-6 w-6 text-white"
                        fill="#4CAF50"
                      />
                      Access to All Metrics
                    </div>
                    <ul className="ml-auto w-fit text-[8px] font-semibold text-[#898989] md:text-xs lg:text-sm">
                      <li>&bull; Number of Shares</li>
                      <li>&bull; Number of Downloads</li>
                      <li>&bull; Location of Download</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <button className="mt-7 hidden lg:block">
                <Link
                  href="/premium-subscription"
                  className="w-fit rounded-3xl bg-[#1A52FB] px-4 py-1 font-poppins text-lg text-white"
                >
                  Explore Plans
                </Link>
              </button>
            </AnimatedElement>
            <AnimatedElement slideFrom="right">
              <div className="h-[178px] w-[241px] max-lg:relative max-lg:mx-auto md:h-[260px] md:w-[266px] lg:absolute lg:-bottom-5 lg:right-0 lg:h-[342px] lg:w-[266px] xl:-bottom-10 xl:right-0">
                <Image
                  src={`/assets/OverlayAndPopups/${imageSrc}`}
                  alt="Explore Plans"
                  fill={true}
                  style={{ objectFit: "contain" }}
                />
                <div className="absolute right-3 top-3 hidden lg:block">
                  <div className="rounded-md bg-[#4225f1] px-3 py-2 text-[12px] text-white">
                    {modalActivity} subscribed in last hour
                  </div>
                </div>
                <div className="absolute w-fit whitespace-nowrap border-2 border-dashed border-[#4D4D4D] px-2 py-1 font-montserrat text-[10px] font-extrabold text-[#4D4D4D] max-lg:-bottom-5 max-lg:left-1/2 max-lg:-translate-x-1/2 max-md:-bottom-24 md:text-sm lg:bottom-3 lg:right-10">
                  No Auto Renewal
                </div>
              </div>
            </AnimatedElement>
            <button className="mt-3 md:mt-7 lg:hidden">
              <Link
                href="/premium-subscription"
                className="w-fit rounded-3xl bg-[#1A52FB] px-4 py-1 font-poppins text-lg text-white"
              >
                Explore Plans
              </Link>
            </button>
          </div>
        </div>
        {isResumeDownloading && countDown > 0 && (
          <p className="absolute left-5 top-3 text-center font-montserrat text-xs font-bold text-[#898989] md:text-sm">
            Your Download starts in{" "}
            <span className="h-6 w-6 rounded-full bg-[#1A52FB] p-1 text-white">
              {countDown}
            </span>{" "}
            seconds
          </p>
        )}
      </div>
    </section>
  );
};

interface AnimatedProps {
  children: React.ReactNode;
  slideFrom: string;
}

export const AnimatedElement = ({ children, slideFrom }: AnimatedProps) => {
  const [isVisible, ref] = useIntersectionObserver<HTMLDivElement>(0.3);

  const slideClass =
    slideFrom === "left"
      ? `${isVisible ? "translate-x-0" : "-translate-x-20 md:translate-y-20"}`
      : `${
          isVisible
            ? "translate-x-0 translate-y-0"
            : "translate-x-20 md:translate-x-60 md:translate-y-20"
        }`;

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${slideClass} ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
    >
      {children}
    </div>
  );
};
